<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <!-- Background overlay -->
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
        <!-- Header -->
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
              Attachments - {{ rowData.woNumber || rowData.invoice || 'Row' }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Row Info -->
          <div class="bg-gray-50 p-4 rounded-lg mb-6">
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div v-if="rowData.woNumber">
                <span class="font-medium text-gray-700">Work Order:</span>
                <span class="ml-2">{{ rowData.woNumber }}</span>
              </div>
              <div v-if="rowData.invoice">
                <span class="font-medium text-gray-700">Invoice:</span>
                <span class="ml-2">{{ rowData.invoice }}</span>
              </div>
              <div v-if="rowData.amount">
                <span class="font-medium text-gray-700">Amount:</span>
                <span class="ml-2">${{ formatCurrency(rowData.amount) }}</span>
              </div>
              <div v-if="rowData.status">
                <span class="font-medium text-gray-700">Status:</span>
                <span class="ml-2">{{ rowData.status }}</span>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex justify-center items-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00225E]"></div>
            <span class="ml-2 text-gray-600">Loading attachments...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">Error loading attachments</h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p>{{ error }}</p>
                </div>
                <div class="mt-4">
                  <button @click="loadAttachments" class="bg-blue-100 px-3 py-2 rounded-md text-sm font-medium text-blue-800 hover:bg-blue-200">
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Attachments List -->
          <div v-else>
            <!-- Rejection Reason -->
            <div v-if="isRejected && rejectionReason" class="mb-4 p-4  border border-blue-200 rounded-lg">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-blue-800">Reason for Rejection</h3>
                  <div class="mt-2 text-sm text-blue-700">
                    <p>{{ rejectionReason }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Return Reason -->
            <div v-if="isReturn && returnReason" class="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-yellow-800">Reason for Return</h3>
                  <div class="mt-2 text-sm text-yellow-700">
                    <p>{{ returnReason }}</p>
                  </div>
                </div>
              </div>
            </div>

            

            <!-- Add New Attachment Section -->
            <div class="mb-6 p-4 border-2 border-dashed rounded-lg" :class="isDeclined ? 'border-blue-300 bg-blue-50' : 'border-gray-300'">
              <div class="text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <div class="mt-4">
                  <label for="file-upload" :class="isDeclined ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'">
                    <span class="mt-2 block text-sm font-medium" :class="isDeclined ? 'text-blue-600' : 'text-gray-900'">
                      {{ isDeclined ? 'Upload Disabled' : 'Add new attachment' }}
                    </span>
                    <span class="mt-1 block text-sm" :class="isDeclined ? 'text-blue-500' : 'text-gray-500'">
                      {{ isDeclined ? 'Cannot upload to reject/dispute invoices' : 'Click to upload or drag and drop' }}
                    </span>
                  </label>
                  <input 
                    id="file-upload" 
                    name="file-upload" 
                    type="file" 
                    class="sr-only" 
                    @change="handleFileUpload"
                    :disabled="uploading || isDeclined"
                  />
                </div>
                <div v-if="uploading" class="mt-2">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00225E] mx-auto"></div>
                  <span class="text-sm text-gray-600">Uploading...</span>
                </div>
              </div>
            </div>


            <!-- Existing Attachments -->
            <div v-if="attachments.length > 0">
              <h4 class="text-md font-medium text-gray-900 mb-4">Existing Attachments ({{ attachments.length }})</h4>
              <div class="space-y-3">
                <div 
                  v-for="attachment in attachments" 
                  :key="attachment.id"
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 truncate">{{ attachment.name }}</p>
                      <p class="text-sm text-gray-500">
                        {{ formatFileSize(attachment.size) }} • {{ formatDate(attachment.createdAt) }} 
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center space-x-2">
                    <button 
                      @click="downloadAttachment(attachment)"
                      class="text-[#00225E] hover:text-[#00225E]/80 text-sm font-medium"
                    >
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Attachments -->
            <div v-else class="text-center py-8">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 class="mt-2 text-sm font-medium text-gray-900">No attachments</h3>
              <p class="mt-1 text-sm text-gray-500">Get started by uploading a file.</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button 
            @click="closeModal"
            type="button" 
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#00225E] text-base font-medium text-white hover:bg-[#00225E]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00225E] sm:ml-3 sm:w-auto sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Confirmation Modal - Outside main modal for proper z-index -->
  <div v-if="showConfirmationModal" class="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="confirmation-modal-title" role="dialog" aria-modal="true">
    <!-- Background overlay -->
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="text-lg leading-6 font-medium text-gray-900" id="confirmation-modal-title">
                Confirm Attachment Upload
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  Adding an attachment will restart the approval process for this invoice. Do you want to continue?
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button 
            @click="confirmUpload"
            type="button" 
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#00225E] text-base font-medium text-white hover:bg-[#00225E]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00225E] sm:ml-3 sm:w-auto sm:text-sm"
          >
            Continue
          </button>
          <button 
            @click="cancelUpload"
            type="button" 
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";

export default {
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    rowData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'refresh'],
  setup(props, { emit }) {
    const store = useStore();
    const attachments = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const uploading = ref(false);
    const showConfirmationModal = ref(false);
    const pendingFile = ref(null);

    // Check if the invoice status contains "Reject/Dispute" (using "contains" logic)
    const isDeclined = computed(() => {
      return props.rowData.status && props.rowData.status.toLowerCase().includes('reject');
    });

    // Check if status is Rejected
    const isRejected = computed(() => {
      return props.rowData.status && props.rowData.status.toLowerCase().includes('reject');
    });

    // Check if status is Return
    const isReturn = computed(() => {
      return props.rowData.status && props.rowData.status.toLowerCase().includes('return');
    });

    // Get rejection reason
    const rejectionReason = computed(() => {
      return props.rowData.reasonForRejection || '';
    });

    // Get return reason
    const returnReason = computed(() => {
      return props.rowData.reasonForReturn || '';
    });


    const loadAttachments = async () => {
      if (!props.rowData.rowId || !props.rowData.sheetId) {
        error.value = "Row ID and Sheet ID are required";
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        const response = await store.dispatch('getRowAttachments', {
          rowId: props.rowData.rowId,
          sheetId: props.rowData.sheetId
        });

        if (response.ok) {
          attachments.value = response.data;
        } else {
          error.value = response.error || "Failed to load attachments";
        }
      } catch (err) {
        error.value = "Failed to load attachments";
        console.error("Error loading attachments:", err);
      } finally {
        loading.value = false;
      }
    };

    const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      console.log('File selected:', file.name);

      // Check if invoice is declined
      if (isDeclined.value) {
        error.value = "Cannot upload attachments to reject/dispute invoices";
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        error.value = "File size must be less than 10MB";
        return;
      }

      // Always show confirmation modal for any attachment upload
      console.log('Showing confirmation modal for attachment upload');
      pendingFile.value = file;
      showConfirmationModal.value = true;
      console.log('showConfirmationModal set to:', showConfirmationModal.value);
      // Reset the file input
      event.target.value = '';
    };

    const performUpload = async (file) => {
      uploading.value = true;
      error.value = null;

      try {
        const response = await store.dispatch('uploadRowAttachment', {
          rowId: props.rowData.rowId,
          sheetId: props.rowData.sheetId,
          file: file
        });

        if (response.ok) {
          // Add the new attachment to the list
          attachments.value.push(response.data);
          
          // Emit refresh event to parent to reload table data
          emit('refresh');
          
          // Close modal after successful upload
          setTimeout(() => {
            closeModal();
          }, 1000); // Give user 1 second to see the success
        } else {
          error.value = response.error || "Failed to upload file";
        }
      } catch (err) {
        console.error("Error uploading file:", err);
        error.value = "Failed to upload file";
      } finally {
        uploading.value = false;
      }
    };

    const confirmUpload = async () => {
      showConfirmationModal.value = false;
      if (pendingFile.value) {
        await performUpload(pendingFile.value);
        pendingFile.value = null;
      }
    };

    const cancelUpload = () => {
      showConfirmationModal.value = false;
      pendingFile.value = null;
    };

    const downloadAttachment = (attachment) => {
      window.open(attachment.url, '_blank');
    };


    const closeModal = () => {
      emit('close');
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // Load attachments when modal opens
    watch(() => props.isOpen, (isOpen) => {
      if (isOpen) {
        loadAttachments();
      }
    });

    return {
      attachments,
      loading,
      error,
      uploading,
      isRejected,
      isReturn,
      rejectionReason,
      returnReason,
      isDeclined,
      showConfirmationModal,
      pendingFile,
      loadAttachments,
      handleFileUpload,
      performUpload,
      confirmUpload,
      cancelUpload,
      downloadAttachment,
      closeModal,
      formatCurrency,
      formatFileSize,
      formatDate
    };
  }
};
</script>
